import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { OTAQ_QRUPLARI } from '../data/otaqlar';
import './OtaqSecici.css';

/**
 * Otaq seçimi.
 *
 * Əvvəl adi <select> idi. Telefonda onun siyahısını brauzerin özü çəkir —
 * künclərin yuvarlaqlığı, fon, sətir hündürlüyü bizdən asılı deyil və
 * CSS ilə dəyişmir. Həm də 80-dən çox otağı barmaqla sürüşdürmək lazım
 * gəlirdi. Ona görə siyahı öz əlimizdədir: axtarış sahəsi var, künclər
 * sistemin qalan hissəsi ilə eyni ölçüdədir.
 */
export default function OtaqSecici({ value, onChange, placeholder, className = '' }) {
  const [acib, setAcib] = useState(false);
  const [axtar, setAxtar] = useState('');
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  const yerleshdir = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const altBoshluq = vh - r.bottom;
    const yuxariAc = altBoshluq < 260 && r.top > altBoshluq;
    setPos({
      left: r.left,
      width: r.width,
      maxH: Math.max(180, (yuxariAc ? r.top : altBoshluq) - 16),
      top: yuxariAc ? undefined : r.bottom + 4,
      bottom: yuxariAc ? vh - r.top + 4 : undefined,
    });
  }, []);

  useEffect(() => {
    if (!acib) { setPos(null); return; }
    yerleshdir();
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    window.addEventListener('scroll', yerleshdir, true);
    window.addEventListener('resize', yerleshdir);
    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', yerleshdir, true);
      window.removeEventListener('resize', yerleshdir);
    };
  }, [acib, yerleshdir]);

  useEffect(() => {
    const bagla = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setAcib(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setAcib(false); };
    document.addEventListener('mousedown', bagla);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', bagla);
      document.removeEventListener('keydown', esc);
    };
  }, []);

  /* Axtarış həm otaq adına, həm qrup adına baxır: "lab" yazanda
     bütün laboratoriyalar, "S2" yazanda ikinci mərtəbə çıxır. */
  const sorgu = axtar.trim().toLocaleLowerCase('az');
  const qruplar = OTAQ_QRUPLARI
    .map(g => {
      if (!sorgu) return g;
      if (g.qrup.toLocaleLowerCase('az').includes(sorgu)) return g;
      const otaqlar = g.otaqlar.filter(o => o.toLocaleLowerCase('az').includes(sorgu));
      return otaqlar.length ? { ...g, otaqlar } : null;
    })
    .filter(Boolean);

  const sec = (o) => { onChange(o); setAcib(false); setAxtar(''); };

  return (
    <div className={'otq-wrap ' + className} ref={wrapRef}>
      <button type="button" className={'otq-btn' + (acib ? ' acib' : '')}
              onClick={() => setAcib(o => !o)} aria-expanded={acib}>
        <span className={'otq-deyer' + (value ? '' : ' bosh')}>{value || placeholder}</span>
        {value && (
          <span className="otq-temizle" role="button" tabIndex={-1}
                title="Təmizlə"
                onClick={(e) => { e.stopPropagation(); onChange(''); }}>×</span>
        )}
        <svg className="otq-ox" viewBox="0 0 24 24" width="14" height="14" fill="none"
             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {acib && pos && createPortal(
        <>
          <div className="otq-ortu" onClick={() => setAcib(false)} />
          <div className="otq-panel" ref={panelRef}
               style={{
                 left: pos.left, width: pos.width, maxHeight: pos.maxH,
                 ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
               }}>
            <div className="otq-axtar">
              <input ref={inputRef} value={axtar} onChange={e => setAxtar(e.target.value)}
                     placeholder="Otaq axtar…" />
            </div>
            <div className="otq-siyahi">
              {qruplar.length === 0 ? (
                <div className="otq-bosh">Tapılmadı</div>
              ) : qruplar.map(g => (
                <div key={g.qrup} className="otq-qrup">
                  <div className="otq-qrup-ad">{g.qrup}</div>
                  {g.otaqlar.map(o => (
                    <button key={o} type="button"
                            className={'otq-setir' + (o === value ? ' secili' : '')}
                            onClick={() => sec(o)}>{o}</button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
